"use client"
import { useState } from "react"
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Alert,
} from "@mui/material"
import { Add, Edit, Delete, Warning, LocalPharmacy, ShoppingCart } from "@mui/icons-material"
import { useApp } from "../contexts/AppContext"

export default function PharmacyManagement({ user }) {
  const { state, actions } = useApp()

  const [open, setOpen] = useState(false)
  const [orderOpen, setOrderOpen] = useState(false)
  const [selectedMedicine, setSelectedMedicine] = useState(null)
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    manufacturer: "",
    batchNumber: "",
    expiryDate: "",
    quantity: 0,
    minStock: 0,
    price: 0,
  })
  const [orderData, setOrderData] = useState({
    patientName: "",
    patientId: "",
    medicines: [{ name: "", quantity: 0, price: 0 }],
  })

  const categories = ["Analgesic", "Antibiotic", "Antacid", "Vitamin", "Antiseptic", "Other"]

  const handleOpen = (medicine = null) => {
    if (medicine) {
      setSelectedMedicine(medicine)
      setFormData(medicine)
    } else {
      setSelectedMedicine(null)
      setFormData({
        name: "",
        category: "",
        manufacturer: "",
        batchNumber: "",
        expiryDate: "",
        quantity: 0,
        minStock: 0,
        price: 0,
      })
    }
    setOpen(true)
  }

  const handleOrderOpen = () => {
    setOrderData({
      patientName: "",
      patientId: "",
      medicines: [{ name: "", quantity: 0, price: 0 }],
    })
    setOrderOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setOrderOpen(false)
    setSelectedMedicine(null)
  }

  const handleSave = () => {
    const status = formData.quantity <= formData.minStock ? "Low Stock" : "In Stock"

    if (selectedMedicine) {
      actions.updateMedicine({ ...formData, id: selectedMedicine.id, status })
    } else {
      actions.addMedicine({
        ...formData,
        status,
      })
    }
    handleClose()
  }

  const handleOrderSave = () => {
    const totalAmount = orderData.medicines.reduce((sum, med) => sum + med.quantity * med.price, 0)
    const newOrder = {
      id: state.orders.length + 1,
      ...orderData,
      totalAmount,
      status: "Pending",
      date: new Date().toISOString().split("T")[0],
    }
    actions.addOrder(newOrder)
    handleClose()
  }

  const handleDelete = (id) => {
    actions.deleteMedicine(id)
  }

  const addOrderMedicine = () => {
    setOrderData({
      ...orderData,
      medicines: [...orderData.medicines, { name: "", quantity: 0, price: 0 }],
    })
  }

  const updateOrderMedicine = (index, field, value) => {
    const updatedMedicines = orderData.medicines.map((med, i) => (i === index ? { ...med, [field]: value } : med))
    setOrderData({ ...orderData, medicines: updatedMedicines })
  }

  const removeOrderMedicine = (index) => {
    setOrderData({
      ...orderData,
      medicines: orderData.medicines.filter((_, i) => i !== index),
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "In Stock":
        return "success"
      case "Low Stock":
        return "warning"
      case "Out of Stock":
        return "error"
      default:
        return "default"
    }
  }

  const lowStockMedicines = state.medicines.filter((m) => m.quantity <= m.minStock)
  const totalMedicines = state.medicines.length
  const totalValue = state.medicines.reduce((sum, med) => sum + med.quantity * med.price, 0)

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Pharmacy Management</Typography>
        <Box>
          <Button variant="outlined" startIcon={<ShoppingCart />} onClick={handleOrderOpen} sx={{ mr: 2 }}>
            New Order
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
            Add Medicine
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {lowStockMedicines.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {lowStockMedicines.length} medicine(s) are running low on stock:{" "}
            {lowStockMedicines.map((m) => m.name).join(", ")}
          </Typography>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <LocalPharmacy sx={{ fontSize: 40, color: "primary.main", mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Medicines
                  </Typography>
                  <Typography variant="h4">{totalMedicines}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Warning sx={{ fontSize: 40, color: "warning.main", mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Low Stock Items
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {lowStockMedicines.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Inventory Value
              </Typography>
              <Typography variant="h4" color="success.main">
                ₹{totalValue.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Medicine Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Manufacturer</TableCell>
              <TableCell>Batch No.</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Min Stock</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.medicines.map((medicine) => (
              <TableRow key={medicine.id}>
                <TableCell>{medicine.name}</TableCell>
                <TableCell>{medicine.category}</TableCell>
                <TableCell>{medicine.manufacturer}</TableCell>
                <TableCell>{medicine.batchNumber}</TableCell>
                <TableCell>{medicine.expiryDate}</TableCell>
                <TableCell>{medicine.quantity}</TableCell>
                <TableCell>{medicine.minStock}</TableCell>
                <TableCell>₹{medicine.price}</TableCell>
                <TableCell>
                  <Chip label={medicine.status} color={getStatusColor(medicine.status)} size="small" />
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(medicine)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(medicine.id)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Medicine Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{selectedMedicine ? "Edit Medicine" : "Add New Medicine"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Medicine Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Batch Number"
                value={formData.batchNumber}
                onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={formData.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price (₹)"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: Number.parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: Number.parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Stock Level"
                type="number"
                value={formData.minStock}
                onChange={(e) => setFormData({ ...formData, minStock: Number.parseInt(e.target.value) })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedMedicine ? "Update" : "Add"} Medicine
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Order Dialog */}
      <Dialog open={orderOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Create New Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Patient Name"
                value={orderData.patientName}
                onChange={(e) => setOrderData({ ...orderData, patientName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Patient ID"
                value={orderData.patientId}
                onChange={(e) => setOrderData({ ...orderData, patientId: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Medicines
              </Typography>
              {orderData.medicines.map((medicine, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      select
                      label="Medicine"
                      value={medicine.name}
                      onChange={(e) => {
                        const selectedMed = state.medicines.find((m) => m.name === e.target.value)
                        updateOrderMedicine(index, "name", e.target.value)
                        if (selectedMed) {
                          updateOrderMedicine(index, "price", selectedMed.price)
                        }
                      }}
                      SelectProps={{ native: true }}
                    >
                      <option value="">Select Medicine</option>
                      {state.medicines.map((med) => (
                        <option key={med.id} value={med.name}>
                          {med.name} - ₹{med.price}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      value={medicine.quantity}
                      onChange={(e) => updateOrderMedicine(index, "quantity", Number.parseInt(e.target.value))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label="Price"
                      type="number"
                      value={medicine.price}
                      onChange={(e) => updateOrderMedicine(index, "price", Number.parseFloat(e.target.value))}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => removeOrderMedicine(index)}
                      disabled={orderData.medicines.length === 1}
                      fullWidth
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}
              <Button variant="outlined" onClick={addOrderMedicine}>
                Add Medicine
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">
                Total Amount: ₹
                {orderData.medicines.reduce((sum, med) => sum + med.quantity * med.price, 0).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleOrderSave} variant="contained">
            Create Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
